import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { Certificate } from '@/types';
import { formatDistanceToNow } from 'date-fns';

/**
 * Generate a verification certificate PDF
 * @param certificate Certificate data
 * @param verificationUrl URL to verification page
 */
export async function generateCertificatePDF(
  certificate: Certificate,
  verificationUrl: string
): Promise<Blob> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Generate QR code
  const qrCodeDataUrl = await QRCode.toDataURL(verificationUrl, {
    width: 200,
    margin: 2,
  });

  // Colors
  const primaryColor = '#2563eb';
  const goldColor = '#eab308';
  const grayColor = '#6b7280';

  // Header
  pdf.setFillColor(primaryColor);
  pdf.rect(0, 0, 210, 40, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.text('BaseProof', 105, 18, { align: 'center' });

  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Certificate of Authenticity', 105, 28, { align: 'center' });

  // Certificate ID
  pdf.setTextColor(goldColor);
  pdf.setFontSize(12);
  pdf.text(`Certificate #${certificate.id}`, 105, 50, { align: 'center' });

  // Document Title
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  const titleLines = pdf.splitTextToSize(certificate.title, 160);
  pdf.text(titleLines, 105, 65, { align: 'center' });

  let yPos = 85;

  // Details
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');

  const addField = (label: string, value: string) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(grayColor);
    pdf.text(label + ':', 20, yPos);

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    const valueLines = pdf.splitTextToSize(value, 130);
    pdf.text(valueLines, 70, yPos);

    yPos += valueLines.length * 6;
  };

  addField('Document Hash', certificate.documentHash);
  addField('Certified By', certificate.issuer);
  addField('Current Owner', certificate.owner);
  addField('Certification Date', new Date(Number(certificate.certificationTimestamp) * 1000).toLocaleString());

  const timeAgo = formatDistanceToNow(new Date(Number(certificate.certificationTimestamp) * 1000), {
    addSuffix: true,
  });
  addField('Time Since Certification', timeAgo);

  if (certificate.originalFilename) {
    addField('Original Filename', certificate.originalFilename);
  }

  yPos += 5;

  // Status
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');

  if (certificate.isRevoked) {
    pdf.setTextColor(220, 38, 38);
    pdf.text('STATUS: REVOKED', 20, yPos);
  } else if (certificate.expirationDate > 0n && Number(certificate.expirationDate) < Date.now() / 1000) {
    pdf.setTextColor(234, 179, 8);
    pdf.text('STATUS: EXPIRED', 20, yPos);
  } else {
    pdf.setTextColor(22, 163, 74);
    pdf.text('STATUS: ACTIVE', 20, yPos);
  }

  yPos += 10;

  // QR Code
  pdf.addImage(qrCodeDataUrl, 'PNG', 75, yPos, 60, 60);
  yPos += 65;

  // Verification URL
  pdf.setFontSize(9);
  pdf.setTextColor(grayColor);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Verify this certificate at:', 105, yPos, { align: 'center' });
  yPos += 5;
  pdf.setTextColor(primaryColor);
  pdf.text(verificationUrl, 105, yPos, { align: 'center' });

  // Footer
  pdf.setFontSize(8);
  pdf.setTextColor(grayColor);
  pdf.setFont('helvetica', 'italic');
  pdf.text(
    'This certificate is cryptographically secured on the Base blockchain.',
    105,
    280,
    { align: 'center' }
  );
  pdf.text(
    'Generated by BaseProof - baseproof.xyz',
    105,
    285,
    { align: 'center' }
  );

  return pdf.output('blob');
}

/**
 * Download a blob as a file
 * @param blob Blob to download
 * @param filename Filename
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
